
import {
    Dialog,
    DialogSurface,
    DialogTitle,
    DialogBody,
    DialogActions,
    Button,
    Input,
    Dropdown,
    Option,
    makeStyles,
    tokens,
    Table,
    TableHeader,
    TableRow,
    TableHeaderCell,
    TableBody,
    TableCell,
    Text,
    Spinner,
} from "@fluentui/react-components";
import { AddRegular, DeleteRegular, Search24Regular, ChevronRight20Regular, Person24Regular } from "@fluentui/react-icons";
import { useState, useEffect } from "react";
import { apiUrl } from "../utils/api";

const useStyles = makeStyles({
    surface: {
        width: "min(1080px, 95vw)",
        maxWidth: "1080px",
    },
    content: {
        display: "flex",
        flexDirection: "column",
        gap: "16px",
        marginTop: "16px",
        overflowY: "auto",
        maxHeight: "60vh",
    },
    inputGroup: {
        display: "grid",
        gridTemplateColumns: "1fr 1fr 1fr 1fr auto",
        gap: "8px",
        alignItems: "end",
    },
    field: {
        display: "flex",
        flexDirection: "column",
        gap: "4px",
    },
    table: {
        marginTop: "16px",
    },
    searchContainer: {
        marginBottom: "24px",
        padding: "16px",
        backgroundColor: tokens.colorNeutralBackground2,
        borderRadius: tokens.borderRadiusMedium,
        border: `1px solid ${tokens.colorNeutralStroke2}`,
    },
    searchResults: {
        maxHeight: "200px",
        overflowY: "auto",
        marginTop: "8px",
        backgroundColor: tokens.colorNeutralBackground1,
        border: `1px solid ${tokens.colorNeutralStroke1}`,
        borderRadius: tokens.borderRadiusMedium,
        boxShadow: tokens.shadow4,
    },
    searchResultItem: {
        padding: "8px 12px",
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
        "&:hover": {
            backgroundColor: tokens.colorNeutralBackground1Hover,
        },
        "&:last-child": {
            borderBottom: "none",
        },
    },
});

export type Role =
    | "Ehepartner"
    | "Ehefrau"
    | "Ehemann"
    | "Partner(in)"
    | "Sohn"
    | "Tochter"
    | "Kind";

export const ROLES: Role[] = [
    "Ehepartner",
    "Ehefrau",
    "Ehemann",
    "Partner(in)",
    "Sohn",
    "Tochter",
    "Kind",
];

export interface HouseholdMember {
    id?: number;
    lastname: string;
    firstname: string;
    role: Role | "";
    birthdate: string;
}

interface HouseholdDialogProps {
    open: boolean;
    onClose: () => void;
    onConfirm: (members: HouseholdMember[]) => void;
    initialMembers?: HouseholdMember[];
}

export const HouseholdDialog = ({
    open,
    onClose,
    onConfirm,
    initialMembers,
}: HouseholdDialogProps) => {
    const styles = useStyles();
    const [members, setMembers] = useState<HouseholdMember[]>([]);

    useEffect(() => {
        if (open) {
            setMembers(initialMembers || []);
        } else {
            setMembers([]);
            setCurrentMember({
                lastname: "",
                firstname: "",
                role: "",
                birthdate: "",
            });
        }
    }, [open, initialMembers]);

    const [currentMember, setCurrentMember] = useState<HouseholdMember>({
        lastname: "",
        firstname: "",
        role: "",
        birthdate: "",
    });

    // Search State
    const [searchQuery, setSearchQuery] = useState("");
    const [searchResults, setSearchResults] = useState<any[]>([]);
    const [isSearching, setIsSearching] = useState(false);

    const handleSearch = async () => {
        if (!searchQuery) return;
        setIsSearching(true);
        try {
            // Search for contacts (defaulting to 'Privat' as they are most likely household members)
            const res = await fetch(apiUrl("contact", { search: searchQuery, limit: 50 }));
            const data = await res.json();
            setSearchResults(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error("Failed to search contacts", err);
            setSearchResults([]);
        } finally {
            setIsSearching(false);
        }
    };

    const selectContact = (contact: any) => {
        setCurrentMember(prev => ({
            ...prev,
            lastname: contact.lastname || "",
            firstname: contact.vorname || contact.firstName || "", // Handle different casing from backend
            birthdate: contact.birthDate || "",
            // Role must still be selected manually
        }));
        // Reset search
        setSearchQuery("");
        setSearchResults([]);
    };

    const handleAdd = () => {
        if (
            !currentMember.lastname ||
            !currentMember.firstname ||
            !currentMember.role ||
            !currentMember.birthdate
        ) {
            // Basic validation
            return;
        }
        setMembers([...members, currentMember]);
        setCurrentMember({
            lastname: "",
            firstname: "",
            role: "",
            birthdate: "",
        });
    };

    const handleDelete = (index: number) => {
        setMembers(members.filter((_, i) => i !== index));
    };

    const handleConfirm = () => {
        onConfirm(members);
        setMembers([]);
        onClose();
    };

    return (
        <Dialog open={open} onOpenChange={(_, d) => !d.open && onClose()}>
            <DialogSurface className={styles.surface}>
                <DialogTitle>Personen im Haushalt hinzufügen</DialogTitle>
                <DialogBody className={styles.content}>

                    {/* Search Section */}
                    <div className={styles.searchContainer}>
                        <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600 }}>
                            Bestehenden Kontakt auswählen (optional)
                        </label>
                        <div style={{ display: "flex", gap: "8px" }}>
                            <Input
                                value={searchQuery}
                                onChange={(_, d) => setSearchQuery(d.value)}
                                placeholder="Kontakt suchen..."
                                contentAfter={
                                    isSearching ? <Spinner size="tiny" /> : <Search24Regular onClick={handleSearch} style={{ cursor: 'pointer' }} />
                                }
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') handleSearch();
                                }}
                                style={{ flex: 1 }}
                            />
                            <Button onClick={handleSearch} disabled={isSearching}>Suchen</Button>
                        </div>
                        {searchResults.length > 0 && (
                            <div className={styles.searchResults}>
                                {searchResults.map((result) => (
                                    <div
                                        key={result.id}
                                        className={styles.searchResultItem}
                                        onClick={() => selectContact(result)}
                                    >
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            <Person24Regular />
                                            <Text>
                                                {result.lastname}, {result.vorname ?? result.firstName}
                                                {result.birthDate && <span style={{ color: tokens.colorNeutralForeground3, fontSize: '12px', marginLeft: '8px' }}>({result.birthDate})</span>}
                                            </Text>
                                        </div>
                                        <ChevronRight20Regular />
                                    </div>
                                ))}
                            </div>
                        )}
                        {searchResults.length === 0 && searchQuery && !isSearching && searchResults !== undefined && (
                            // Only show if we actually searched and got nothing (we might need a flag "hasSearched")
                            // For simplicity omitting aggressive "nothing found" unless we track "searchPerformed" state
                            null
                        )}
                    </div>

                    <div className={styles.inputGroup}>
                        <div className={styles.field}>
                            <label htmlFor="lastname">Nachname</label>
                            <Input
                                id="lastname"
                                value={currentMember.lastname}
                                onChange={(_, d) =>
                                    setCurrentMember((p) => ({ ...p, lastname: d.value }))
                                }
                                placeholder="Mustermann"
                            />
                        </div>
                        <div className={styles.field}>
                            <label htmlFor="firstname">Vorname</label>
                            <Input
                                id="firstname"
                                value={currentMember.firstname}
                                onChange={(_, d) =>
                                    setCurrentMember((p) => ({ ...p, firstname: d.value }))
                                }
                                placeholder="Max"
                            />
                        </div>
                        <div className={styles.field}>
                            <label htmlFor="role">Rolle</label>
                            <Dropdown
                                id="role"
                                value={currentMember.role}
                                placeholder="Rolle wählen"
                                onOptionSelect={(_, d) =>
                                    setCurrentMember((p) => ({
                                        ...p,
                                        role: (d.optionValue as Role) ?? "",
                                    }))
                                }
                            >
                                {ROLES.map((role) => (
                                    <Option key={role} value={role}>
                                        {role}
                                    </Option>
                                ))}
                            </Dropdown>
                        </div>
                        <div className={styles.field}>
                            <label htmlFor="birthdate">Geburtsdatum</label>
                            <Input
                                id="birthdate"
                                type="date"
                                value={currentMember.birthdate}
                                onChange={(_, d) =>
                                    setCurrentMember((p) => ({ ...p, birthdate: d.value }))
                                }
                            />
                        </div>
                        <Button appearance="primary" icon={<AddRegular />} onClick={handleAdd} disabled={!currentMember.lastname || !currentMember.firstname || !currentMember.role || !currentMember.birthdate}>
                        </Button>
                    </div>

                    <div className={styles.table}>
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHeaderCell>Name</TableHeaderCell>
                                    <TableHeaderCell>Rolle</TableHeaderCell>
                                    <TableHeaderCell>Geburtsdatum</TableHeaderCell>
                                    <TableHeaderCell />
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {members.map((member, i) => (
                                    <TableRow key={i}>
                                        <TableCell>
                                            {member.lastname}, {member.firstname}
                                        </TableCell>
                                        <TableCell>{member.role}</TableCell>
                                        <TableCell>{member.birthdate}</TableCell>
                                        <TableCell>
                                            <Button
                                                icon={<DeleteRegular />}
                                                appearance="subtle"
                                                onClick={() => handleDelete(i)}
                                            />
                                        </TableCell>
                                    </TableRow>
                                ))}
                                {members.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={4} style={{ textAlign: "center", color: tokens.colorNeutralForeground4 }}>
                                            Noch keine Personen hinzugefügt.
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </div>
                </DialogBody>
                <DialogActions>
                    <Button appearance="secondary" onClick={onClose}>
                        Abbrechen
                    </Button>
                    <Button
                        appearance="primary"
                        onClick={handleConfirm}
                        disabled={members.length === 0}
                    >
                        Übernehmen
                    </Button>
                </DialogActions>
            </DialogSurface>
        </Dialog>
    );
};
